import { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { GridRowModes, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { v4 as uuidv4 } from 'uuid';
import { Box, Typography, Paper } from '@mui/material';
import dayjs from 'dayjs';

import { dataGridPremiumColumns } from '../../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  DatagridPremiumUi,
  InputUI,
  DialogUI,
  ButtonUI,
  AutoCompleteSelectUI,
} from '../../Interface';

import documentService from '../../../service/newDoc.service';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import warehouseService from '../../../service/warehouse.service';

const ProductionBatchOutDialog = ({
  open,
  onClose,
  disabled = false,
  //selectedLineIndex,
  //docLine,
  selectedLine,
  whsCode,
  defaultBatchLine,
  onSave,
}) => {
  const apiRef = useGridApiRef();
  const [batches, setBatches] = useState([]);
  const [batchOut, setBatchOut] = useState([]);
  const [binOut, setBinOut] = useState([]);
  const [binOptions, setBinOptions] = useState([]);
  const { selectedLineIndex } = selectedLine || {};
  const isBinManaged = binOptions.length > 0;
  const isBatchManaged = selectedLine?.isBatchManaged ?? false;

  const getItemStock = () => {
    documentService
      .getItemStock({
        whsCode,
        itemCode: selectedLine?.itemCode,
      })
      .then((data) => {
        setBatches(data);
      });
  };

  useEffect(() => {
    if (!open || !whsCode) return;
    warehouseService.getBins(whsCode).then((data) => {
      const options = (data ?? []).map((bin) => ({
        label: bin.binCode,
        value: bin.binId,
        binCode: bin.binCode,
      }));
      setBinOptions(options);
    });
  }, [open, whsCode]);

  useEffect(() => {
    if (!whsCode) {
      addNotification({
        content: 'Nincs megadott raktár!',
        type: NotificationType.WARNING,
      });
    }
    if (!selectedLine?.itemCode) return;
    getItemStock();
    const nextBatchOut = selectedLine?.docLineBatchSn ? [...selectedLine.docLineBatchSn] : [];
    const nextBinOut = selectedLine?.docLineBin ? [...selectedLine.docLineBin] : [];
    setBatchOut(nextBatchOut);
    setBinOut(normalizeBinOut(nextBatchOut, nextBinOut));
  }, [whsCode, selectedLine?.itemCode, open]);

  if (typeof selectedLineIndex !== 'number') return null;

  const getBatchBinKey = (batchLine) => {
    if (!batchLine) return null;
    return batchLine.id ?? null;
  };

  const normalizeBinOut = (batchLines, binLines) => {
    if (!Array.isArray(binLines) || binLines.length === 0) return [];
    const normalized = [...binLines];
    const emptyBatchLines = batchLines ?? [];
    const used = new Set();

    for (let i = 0; i < normalized.length; i += 1) {
      const line = normalized[i];
      if (line?.snBinId) continue;
      const batchLine = emptyBatchLines.find((b) => b?.id && !used.has(b.id));
      if (!batchLine) continue;
      normalized[i] = { ...line, snBinId: batchLine.id };
      used.add(batchLine.id);
    }

    return normalized;
  };

  const upsertBinForBatch = (batchKey, binCode, binId, quantity) => {
    const bins = [...binOut];
    const idx = bins.findIndex((b) => b.snBinId === batchKey);
    const updated = {
      ...(idx >= 0 ? bins[idx] : { id: uuidv4() }),
      binCode,
      binId,
      snBinId: batchKey,
      quantity,
    };

    if (idx >= 0) {
      bins[idx] = updated;
    } else {
      bins.push(updated);
    }

    setBinOut(bins);
  };

  const handleAddClick = (row) => {
    if (isBatchManaged) {
      const newBatchLine = {
        ...defaultBatchLine,
        id: uuidv4(),
        batchSn: row.batchSn,
      };
      setBatchOut((prev) => [...prev, newBatchLine]);
      if (isBinManaged) {
        const rowBinCode = row.binCode ?? '';
        const rowBinId = row.binId ?? null;
        if (rowBinCode || rowBinId) {
          const batchKey = getBatchBinKey(newBatchLine);
          upsertBinForBatch(batchKey, rowBinCode, rowBinId, newBatchLine.quantity ?? '');
        }
      }
      setTimeout(() => {
        apiRef.current.startRowEditMode({ id: newBatchLine.id });
      }, 0);
    } else {
      const newBinLine = {
        id: uuidv4(),
        binCode: row.binCode ?? '',
        binId: row.binId ?? null,
        snBinId: null,
        quantity: row.quantity ?? '',
      };
      setBinOut((prev) => [...prev, newBinLine]);
      setTimeout(() => {
        apiRef.current.startRowEditMode({ id: newBinLine.id });
      }, 0);
    }
  };

  const validateSelections = () => {
    const missingBatch = isBatchManaged
      ? batchOut.some((line) => {
          const raw = line?.batchSn;
          const normalized = typeof raw === 'string' ? raw.trim() : raw;
          return normalized === null || normalized === undefined || normalized === '';
        })
      : false;

    const missingBin = isBinManaged
      ? (isBatchManaged ? batchOut : binOut).some((line) => {
          const batchKey = isBatchManaged ? getBatchBinKey(line) : line?.id ?? null;
          const bin = isBatchManaged ? binOut.find((b) => b.snBinId === batchKey) : line;
          const raw = bin?.binCode;
          const normalized = typeof raw === 'string' ? raw.trim() : raw;
          return normalized === null || normalized === undefined || normalized === '';
        })
      : false;

    if (missingBatch) {
      addNotification({
        content: 'Sarzs megadása kötelező!',
        type: NotificationType.WARNING,
      });
    }

    if (missingBin) {
      addNotification({
        content: 'Rakhely megadása kötelező!',
        type: NotificationType.WARNING,
      });
    }

    return !missingBatch && !missingBin;
  };

  const columns = (push) => {
    return dataGridPremiumColumns([
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id, row }) => {
          return [
            <GridActionsCellItem
              icon={<AddIcon />}
              label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleAddClick(row, push);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
      ...(isBatchManaged
        ? [
            {
              field: 'batchSn',
              headerName: 'Sarzs',
              type: 'string',
              sortable: false,
            },
          ]
        : []),
      ...(isBinManaged
        ? [
            {
              field: 'binCode',
              headerName: 'Rakhely',
              type: 'string',
              sortable: false,
            },
          ]
        : []),
      {
        field: 'effectiveQty',
        headerName: 'Mennyiség',
        type: 'number',
        sortable: false,
      },
      ...(isBatchManaged
        ? [
            {
              field: 'lot',
              headerName: 'LOT',
              type: 'string',
              sortable: false,
            },
          ]
        : []),
      ...(isBatchManaged
        ? [
            {
              field: 'supplierId',
              headerName: 'Beszállító azon.',
              type: 'string',
              sortable: false,
            },
          ]
        : []),
      ...(isBatchManaged
        ? [
            {
              field: 'expDate',
              headerName: 'Lejárat',
              type: 'date',
              sortable: false,
              valueGetter: (_data, row) => {
                return row.expDate ? new Date(row.expDate) : null;
              },
              renderCell: (data) => {
                return data.row.expDate ? dayjs(data.row.expDate).format('YYYY.MM.DD') : '';
              },
            },
            {
              field: 'mnfDate',
              headerName: 'Gyártás',
              type: 'date',
              sortable: false,
              valueGetter: (_data, row) => {
                return row.mnfDate ? new Date(row.mnfDate) : null;
              },
              renderCell: (data) => {
                return data.row.mnfDate ? dayjs(data.row.mnfDate).format('YYYY.MM.DD') : '';
              },
            },
            {
              field: 'batchAttribute1',
              headerName: 'Attribútum 1',
              type: 'string',
              sortable: false,
            },
            {
              field: 'batchAttribute2',
              headerName: 'Attribútum 2',
              type: 'string',
              sortable: false,
            },
            {
              field: 'comment',
              headerName: 'Megjegyzés',
              type: 'string',
              sortable: false,
            },
            {
              field: 'price',
              headerName: 'Ár',
              type: 'string',
              sortable: false,
              valueGetter: (data, row) => {
                return row.price ?? '';
              },
            },
            {
              field: 'other',
              headerName: 'Egyéb',
              type: 'string',
              sortable: false,
            },
          ]
        : []),
    ]);
  };

  const binStockColumns = (push) =>
    dataGridPremiumColumns([
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ row }) => {
          return [
            <GridActionsCellItem
              icon={<AddIcon />}
              label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleAddClick(row, push);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
      {
        field: 'binCode',
        headerName: 'Rakhely',
        type: 'string',
        sortable: false,
      },
      {
        field: 'effectiveQty',
        headerName: 'Mennyiség',
        type: 'number',
        sortable: false,
      },
    ]);

  const batchOutCols = () => {
    if (!isBatchManaged) {
      return dataGridPremiumColumns([
        {
          field: 'binCode',
          headerName: 'Rakhely',
          type: 'string',
          valueGetter: (_data, row) => row.binCode ?? '',
        },
        {
          field: 'quantity',
          headerName: 'Mennyiség',
          type: 'number',
          editable: true,
          renderEditCell: (data) => {
            const lineIndex = binOut.findIndex((b) => b.id === data.id);
            return (
              <InputUI
                type="number"
                value={binOut[lineIndex]?.quantity ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setBinOut((prev) => prev.map((b, i) => (i === lineIndex ? { ...b, quantity: value } : b)));
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: value,
                  });
                }}
                fullWidth
                size="small"
              />
            );
          },
        },
        {
          field: 'actions',
          type: 'actions',
          headerName: 'Szerk.',
          cellClassName: 'actions',
          sortable: false,
          getActions: ({ id }) => [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setBinOut((prev) => prev.filter((b) => b.id !== id));
              }}
              color="inherit"
              disabled={disabled}
            />,
          ],
        },
      ]);
    }

    return dataGridPremiumColumns([
      {
        field: 'batchSn',
        headerName: 'Sarzs',
        type: 'string',
      },
      ...(isBinManaged
        ? [
            {
              field: 'binCode',
              headerName: 'Rakhely',
              type: 'string',

              valueGetter: (_data, row) => {
                const batchIndex = batchOut.findIndex((b) => b.id === row.id);
                const batchLine = batchOut[batchIndex] ?? null;
                const batchKey = getBatchBinKey(batchLine);
                const bin = binOut.find((b) => b.snBinId === batchKey);
                return bin?.binCode ?? '';
              },
            },
          ]
        : []),
      {
        field: 'quantity',
        headerName: 'Mennyiség',
        type: 'number',
        editable: true,
        renderEditCell: (data) => {
          const batchIndex = batchOut.findIndex((b) => b.id === data.id);
          return (
            <InputUI
              type="number"
              value={batchOut[batchIndex]?.quantity ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setBatchOut((prev) => prev.map((b, i) => (i === batchIndex ? { ...b, quantity: value } : b)));
                if (isBinManaged) {
                  const batchLine = batchOut[batchIndex] ?? null;
                  const batchKey = getBatchBinKey(batchLine);
                  const currentBin = binOut.find((b) => b.snBinId === batchKey);
                  if (currentBin) {
                    upsertBinForBatch(batchKey, currentBin.binCode ?? '', currentBin.binId ?? null, value ?? '');
                  }
                }
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: value,
                });
              }}
              fullWidth
              size="small"
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setBatchOut((prev) => prev.filter((b) => b.id !== id));
              if (isBinManaged) {
                const batchLine = batchOut.find((b) => b.id === id) ?? null;
                const batchKey = getBatchBinKey(batchLine);
                if (batchKey) {
                  setBinOut((prev) => prev.filter((b) => b.snBinId !== batchKey));
                }
              }
            }}
            color="inherit"
            disabled={disabled}
          />,
        ],
      },
    ]);
  };

  return (
    <DialogUI
      open={open}
      onClose={onClose}
      headerContent={isBatchManaged ? 'Sarzs' : 'Rakhely'}
      maxWidth="lg"
      fullWidth
    >
      <Box sx={{ overflowX: 'auto' }}>
        <Box className="align-middle">
          <Paper elevation={1} sx={{ overflow: 'hidden', borderRadius: 2 }}>
            <Box display="flex" gap={2} p={2} alignItems="center" bgcolor="#f5f5f5" mb={2}>
              <Box flex={0.35} minWidth={0} display="flex" alignItems="center">
                <Typography fontWeight="bold">Sor: </Typography>
                <Typography>{selectedLineIndex + 1}</Typography>
              </Box>
              <Box flex={1} minWidth={0} display="flex" alignItems="center">
                <Typography fontWeight="bold">Cikkszám: </Typography>
                <Typography>{selectedLine?.itemCode ?? ''}</Typography>
              </Box>
              <Box flex={1} minWidth={0} display="flex" alignItems="center">
                <Typography fontWeight="bold">Nyitott mennyiség: </Typography>
                <Typography>{selectedLine?.qty ?? ''}</Typography>
              </Box>
              <Box flex={1} minWidth={0} display="flex" alignItems="center">
                <Typography fontWeight="bold">
                  {isBatchManaged ? 'Összesített sarzs: ' : 'Összesített rakhely: '}
                </Typography>
                <Typography>
                  {(isBatchManaged ? batchOut ?? [] : binOut ?? []).reduce(
                    (sum, line) => sum + (Number(line.quantity) || 0),
                    0
                  )}
                </Typography>
              </Box>
            </Box>

            <Box display="flex" gap={2}>
              <Box flex={1} minWidth={0}>
                <DatagridPremiumUi
                  datagridIdentifier={
                    isBinManaged && !isBatchManaged
                      ? DataGridIdentifiers.BinInStockTable
                      : isBatchManaged && !isBinManaged
                        ? DataGridIdentifiers.BatchInStockTable
                        : DataGridIdentifiers.BinBatchInStockTable
                  }
                  height={480}
                  columns={isBatchManaged ? columns() : binStockColumns()}
                  rows={batches ?? []}
                  slots={{
                    toolbar: null,
                  }}
                  onCellDoubleClick={(_params, event) => {
                    event.defaultMuiPrevented = true;
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  getRowId={(row) => row.batchSnId ?? row.binId ?? row.binCode ?? row.id}
                />
              </Box>
              <Box flex={1} minWidth={0}>
                <DatagridPremiumUi
                  apiRef={apiRef}
                  datagridIdentifier={
                    isBinManaged && !isBatchManaged
                      ? DataGridIdentifiers.BinOutTable
                      : isBatchManaged && !isBinManaged
                        ? DataGridIdentifiers.BatchOutTable
                        : DataGridIdentifiers.BinBatchOutTable
                  }
                  height={480}
                  columns={batchOutCols()}
                  rows={isBatchManaged ? batchOut : binOut}
                  editMode="row"
                  onCellDoubleClick={(_params, event) => {
                    event.defaultMuiPrevented = true;
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  onCellClick={(params, event) => {
                    if (disabled) return;
                    const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

                    for (const rowId of rowsInEditMode) {
                      if (params.row.id.toString() !== rowId) {
                        apiRef.current.stopRowEditMode({ id: rowId });
                      }
                    }

                    const rowMode = apiRef.current.getRowMode(params.row.id);
                    if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <ButtonUI
                text="Mentés"
                className="bg-success"
                onClick={() => {
                  if (!validateSelections()) return;
                  if (onSave) {
                    onSave({
                      docLineBatchSn: isBatchManaged ? batchOut : [],
                      docLineBin: binOut,
                    });
                  }
                  onClose();
                }}
              />
              <ButtonUI text="Mégse" color="blue-gray" variant="gradient" onClick={onClose} />
            </Box>
          </Paper>
        </Box>
      </Box>
    </DialogUI>
  );
};

export default ProductionBatchOutDialog;
